import PageProtectedLayout from "../../../components/Page/PageProtectedLayout";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../provider";
import { useLocation } from "react-router-dom";
import { Milestone } from "lucide-react";
import axios from "axios";
import {
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";
import LabWorkflowSteps from "../../../components/Steps/LabWorkflowSteps";
import DiaLabDetails from "../../Onboarding/WaitingResults/DiaLabDetails";
import * as c from "../../../constants";
import ButtonsTextGoTo from "../../../components/Button/ButtonTextGo";
import { useTranslation } from "../../../language";

// Lab visit states that are considered "pending" (not the current active visit)
const INACTIVE_STATES = [1, 2, 3, 5, 6];

export default function StateWaiting() {
  const { state, handleLogout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const labVisitIdFromNav = location.state?.labVisitId;

  const [visits, setVisits] = useState([]);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [order, setOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);

  // Derive the active visit ID — prefer navigation state, fall back to currentVisit
  const activeVisitId = useMemo(
    () => labVisitIdFromNav ?? currentVisit?.id ?? null,
    [labVisitIdFromNav, currentVisit]
  );

  // ── 1. Fetch all lab visits for the user ────────────────────────────────────
  useEffect(() => {
    if (!state?.user?.id) return;

    const fetchVisits = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${c.API_URL}/api/user-lab-visits/`);
        const data = res.data;

        const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
        setVisits(visitsArray);

        // Find the first visit whose state is NOT in INACTIVE_STATES
        const active = visitsArray.find(
          (v) => !INACTIVE_STATES.includes(v.state)
        ) ?? null;
        setCurrentVisit(active);
      } catch (err) {
        console.error("Failed to fetch lab visits:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVisits();
  }, [state?.user?.id]);

  // ── 2. Fetch order items once we have a resolved visit ID ───────────────────
  useEffect(() => {
    if (!state?.user?.id || !activeVisitId) return;

    const fetchOrder = async () => {
      setOrderLoading(true);
      try {
        const res = await axios.get(
          `${c.API_URL}/one-time-orders/by-labvisit/${activeVisitId}/`
        );
        const data = res.data;
        setOrder(Array.isArray(data) ? data : data ? [data] : []);
      } catch (err) {
        console.error("No add-on order items found:", err);
        setOrder([]);
      } finally {
        setOrderLoading(false);
      }
    };

    fetchOrder();
  }, [state?.user?.id, activeVisitId]);

  // ── Resolve which visit record to display ───────────────────────────────────
  const displayVisit = useMemo(() => {
    if (labVisitIdFromNav) {
      // If we arrived with a specific ID, prefer that visit record
      return visits.find((v) => v.id === labVisitIdFromNav) ?? currentVisit;
    }
    return currentVisit;
  }, [labVisitIdFromNav, visits, currentVisit]);

  const labDetails = displayVisit?.laboratory ?? null;
  const orderItems = order?.[0]?.items ?? [];
  const hasOrder = orderItems.length > 0;



  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <PageProtectedLayout headline={t("lab_header7")} specify={t("lab_header8")}>
      <>
        <Grid
          container
          spacing={1}
          sx={{
            maxWidth: 1000,
            mb: 16,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* ── Workflow Stepper ── */}
          <Grid
            size={12}
            sx={{
              maxWidth: 800,
              my: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LabWorkflowSteps stepNumber={3} />
          </Grid>

          {/* ── Status explanation ── */}
          <Grid
            size={{ xs: 12 }}
            sx={{
              mt: 2,
              maxWidth: 600,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              textAlign="center"
              gutterBottom
              sx={{ mb: 2, fontWeight: 400 }}
            >
              {t("onb_subscr28")}
              <br />
              {t("onb_subscr29")}
              <br />
              <br />
              {t("waiting_2")}
            </Typography>
          </Grid>

          {/* ── Lab Visit Date / Time ── */}
          <Grid
            size={{ xs: 12 }}
            sx={{
              maxWidth: { xs: 520, sm: 600 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : displayVisit ? (
              <Paper
                key={displayVisit.id}
                sx={{
                  px: 6,
                  borderRadius: 3,
                  width: "100%",
                  minWidth: { xs: 520, sm: 600 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "grey.C50",
                }}
              >
                <Typography variant="subtitle2" fontWeight={400}>
                  {displayVisit.laboratory?.name}
                </Typography>
                <Box>
                  <DiaLabDetails labDetails={labDetails} />
                </Box>
                <Typography variant="subtitle2" fontWeight={400}>
                  Date: {displayVisit.visit_date}
                </Typography>
                <Typography variant="subtitle2" fontWeight={400}>
                  Time: {displayVisit.visit_time?.slice(0, 5)}
                </Typography>
              </Paper>
            ) : (
              <Typography
                variant="subtitle1"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                No scheduled lab visits found.
              </Typography>
            )}
          </Grid>

          {/* ── Core subscription product ── */}
          <Grid size={12} sx={{ mt: 6, maxWidth: 600 }}>
            <Typography
              variant="subtitle1"
              textAlign="center"
              sx={{ fontWeight: 400 }}
            >
              {t("waiting_1")}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Box
              sx={{
                maxWidth: 600,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  maxWidth: 600,
                  width: "100%",
                  mx: "auto",
                  mt: 1,
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "grey.C100",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "grey.C50",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={500}>
                    1 x Core Health Blood Test
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={500}>
                    included in subscription
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* ── Add-on orders ── */}
          <Grid size={12}>
            <Box
              sx={{
                maxWidth: 600,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
              }}
            >
              {orderLoading ? (
                <CircularProgress size={24} />
              ) : hasOrder ? (
                <Paper
                  elevation={3}
                  sx={{
                    maxWidth: 600,
                    width: "100%",
                    mx: "auto",
                    mt: 1,
                    p: 3,
                    borderRadius: 3,
                    bgcolor: "grey.C100",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    textAlign="center"
                    sx={{ mb: 2 }}
                  >
                    {t("onb_subscr32")}
                  </Typography>

                  <Stack spacing={1.5}>
                    {orderItems.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "grey.C50",
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={500}>
                          {item.product_name?.name ?? "Unnamed Test"}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={500}>
                          €&nbsp;{(item.unit_price_cents / 100).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle2"
                    textAlign="right"
                    fontWeight={600}
                    sx={{ mt: 1 }}
                  >
                    Addon Packages – Total: €&nbsp;
                    {(order[0].total_amount / 100).toFixed(2)}
                  </Typography>

                  {order[0].paid ? (
                    <Typography
                      variant="body2"
                      textAlign="right"
                      sx={{ mt: 1, fontWeight: 500 }}
                    >
                      ✅ Payment confirmed
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      textAlign="right"
                      sx={{ mt: 1, color: "error.main", fontWeight: 500 }}
                    >
                      ❌ Not paid
                    </Typography>
                  )}
                </Paper>
              ) : (
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", mt: 2, fontWeight: 400 }}
                >
                  No Add-On Tests selected for next lab visit.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* ── Re-login hint ── */}
          <Grid
            size={{ xs: 12 }}
            sx={{
              mt: 4,
              maxWidth: 600,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, fontWeight: 400 }}
            >
              {t("onb_subscr33")} <br />
              {t("onb_subscr34")}
            </Typography>
          </Grid>

          {/* ── Go to lab visits ── */}
          <Grid
            size={12}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <Stack
              spacing={2}
              direction="column"
              sx={{
                mb: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                textAlign="center"
                gutterBottom
                sx={{ mb: 4, fontWeight: 400 }}
              >
                {t("lab_start5")} <br />
                {t("lab_start6")}
              </Typography>
              <Stack
                spacing={2}
                direction="row"
                sx={{
                  mb: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Milestone size={18} />
                <ButtonsTextGoTo
                  to="/LabVisitsWelcome"
                  name={t("acc_visit30")}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </>
    </PageProtectedLayout>
  );
}